package com.swetdot.swetgram.ui.picso

import androidx.compose.animation.animateColorAsState
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.Role
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.swetdot.swetgram.domain.PicsoPostItem
import com.swetdot.swetgram.ui.design.*
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.*

/** Viewer chrome has no networking or optimistic state; the confirmed post owns selection. */
@Composable
internal fun PicsoViewerHeader(
    post: PicsoPostItem, busy: Boolean, onDismiss: () -> Unit, onOpenProfile: () -> Unit,
    onDelete: () -> Unit, onReport: () -> Unit,
) {
    val colors = SwetgramThemeTokens.colors
    var menuOpen by remember(post.id) { mutableStateOf(false) }
    Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        PicsoChromeButton(SwetIcons.Close, stringResource(Res.string.picso_close), onDismiss)
        Row(
            Modifier.weight(1f).clip(RoundedCornerShape(16.dp))
                .swetPressable(onClick = onOpenProfile).padding(vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            SwetAvatar(post.authorDisplayName.ifBlank { post.authorUsername }, size = 36.dp, avatarUrl = post.authorAvatarUrl)
            Column(Modifier.weight(1f)) {
                Text("@${post.authorUsername}", color = colors.textPrimary, style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(stringResource(Res.string.picso_title), color = colors.textSecondary, style = MaterialTheme.typography.labelSmall)
            }
        }
        Box {
            PicsoChromeButton(SwetIcons.More, stringResource(Res.string.picso_more), { menuOpen = true }, enabled = !busy)
            DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false },
                containerColor = colors.surface, shape = RoundedCornerShape(20.dp)) {
                DropdownMenuItem(
                    text = { Text(stringResource(if (post.isMine) Res.string.picso_action_delete else Res.string.picso_report)) },
                    leadingIcon = { SwetIcon(if (post.isMine) SwetIcons.Trash else SwetIcons.Flag, colors.textPrimary, null) },
                    onClick = { menuOpen = false; if (post.isMine) onDelete() else onReport() }, enabled = !busy,
                )
            }
        }
    }
}

@Composable
private fun PicsoChromeButton(icon: ImageVector, label: String, onClick: () -> Unit, enabled: Boolean = true) {
    val colors = SwetgramThemeTokens.colors
    Box(Modifier.size(48.dp).clip(CircleShape).background(colors.surface.copy(alpha = 0.8f))
        .swetPressable(onClick, enabled = enabled, feedback = SwetPressFeedback.Scale)
        .semantics { contentDescription = label }
        .graphicsLayer { alpha = if (enabled) 1f else 0.45f }, contentAlignment = Alignment.Center) {
        SwetIcon(icon, colors.textPrimary, null, size = 22.dp)
    }
}

@Composable
internal fun PicsoLifetimeBadge(remaining: String, modifier: Modifier = Modifier) {
    Row(modifier.clip(CircleShape).background(Color.Black.copy(alpha = 0.65f))
        .border(1.dp, Color.White.copy(alpha = 0.12f), CircleShape).padding(horizontal = 10.dp, vertical = 7.dp),
        verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        SwetIcon(SwetIcons.Clock, Color.White.copy(alpha = 0.85f), null, size = 14.dp)
        Text(stringResource(Res.string.picso_remaining_time, remaining), color = Color.White, style = MaterialTheme.typography.labelSmall)
    }
}

@Composable
internal fun PicsoViewerActionDock(
    post: PicsoPostItem, busy: Boolean, onReact: (String) -> Unit,
    onToggleSave: () -> Unit, onShare: () -> Unit, modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors
    val fontScale = LocalDensity.current.fontScale
    val reactionsDescription = stringResource(Res.string.picso_reactions_count, post.reactionsCount.toString())
    val shape = RoundedCornerShape(28.dp)
    BoxWithConstraints(modifier.clip(shape).background(Brush.verticalGradient(listOf(colors.graphite, colors.surface)))
        .border(1.dp, colors.borderStrong.copy(alpha = 0.65f), shape)
        .verticalScroll(rememberScrollState())) {
        val stackAnalytics = stackPicsoAnalytics(maxWidth.value - 32f, fontScale)
        Column(Modifier.fillMaxWidth().padding(16.dp), verticalArrangement = Arrangement.spacedBy(14.dp)) {
            post.caption?.takeIf { it.isNotBlank() }?.let {
                Text(it, color = colors.textPrimary, style = MaterialTheme.typography.bodyMedium)
            }
            if (post.isMine) {
                if (stackAnalytics) {
                    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                        PicsoStat(SwetIcons.Eye, post.viewsCount, stringResource(Res.string.picso_stat_views), Modifier.fillMaxWidth(), wide = true)
                        PicsoStat(SwetIcons.GroupsNavigation, post.uniqueViewersCount, stringResource(Res.string.picso_stat_unique), Modifier.fillMaxWidth(), wide = true)
                    }
                } else {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        PicsoStat(SwetIcons.Eye, post.viewsCount, stringResource(Res.string.picso_stat_views), Modifier.weight(1f))
                        PicsoStat(SwetIcons.GroupsNavigation, post.uniqueViewersCount, stringResource(Res.string.picso_stat_unique), Modifier.weight(1f))
                    }
                }
            }
            Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
                Text(stringResource(Res.string.picso_react_title), color = colors.textSecondary, style = MaterialTheme.typography.labelMedium,
                    modifier = Modifier.weight(1f))
                if (post.reactionsCount > 0) {
                    SwetIcon(SwetIcons.Heart, colors.textSecondary, null, size = 14.dp)
                    Spacer(Modifier.width(5.dp))
                    Text(post.reactionsCount.toString(), color = colors.textSecondary, style = MaterialTheme.typography.labelMedium,
                        modifier = Modifier.semantics { contentDescription = reactionsDescription })
                }
                Box(Modifier.size(20.dp), contentAlignment = Alignment.Center) {
                    if (busy) CircularProgressIndicator(Modifier.size(14.dp), color = colors.textSecondary, strokeWidth = 1.5.dp)
                }
            }
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
                picsoReactionEmojis.forEach { emoji ->
                    PicsoReactionButton(emoji, selected = post.myReaction == emoji, enabled = !busy, onClick = { onReact(emoji) })
                }
            }
            val save: @Composable (Modifier) -> Unit = { actionModifier ->
                PicsoDockAction(
                    icon = if (post.isSaved) SwetIcons.BookmarkFilled else SwetIcons.Bookmark,
                    label = stringResource(if (post.isSaved) Res.string.picso_action_saved else Res.string.picso_action_save),
                    selected = post.isSaved, enabled = !busy, onClick = onToggleSave, modifier = actionModifier,
                )
            }
            val forward: @Composable (Modifier) -> Unit = { actionModifier ->
                PicsoDockAction(SwetIcons.Forward, stringResource(Res.string.picso_share), enabled = !busy,
                    onClick = onShare, primary = true, modifier = actionModifier)
            }
            if (fontScale > 1.3f) {
                Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    save(Modifier.fillMaxWidth()); forward(Modifier.fillMaxWidth())
                }
            } else {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    save(Modifier.weight(1f)); forward(Modifier.weight(1f))
                }
            }
        }
    }
}

internal fun stackPicsoAnalytics(contentWidthDp: Float, fontScale: Float): Boolean =
    contentWidthDp < 300f || fontScale > 1.3f

@Composable
private fun PicsoStat(icon: ImageVector, count: Int, label: String, modifier: Modifier, wide: Boolean = false) {
    val colors = SwetgramThemeTokens.colors
    if (wide) {
        Row(modifier.clip(RoundedCornerShape(16.dp)).background(colors.background.copy(alpha = 0.4f))
            .padding(horizontal = 12.dp, vertical = 10.dp), verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            SwetIcon(icon, colors.textSecondary, null, size = 16.dp)
            Text(count.toString(), color = colors.textPrimary, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.titleSmall)
            Text(label, color = colors.textSecondary, style = MaterialTheme.typography.labelSmall, modifier = Modifier.weight(1f))
        }
        return
    }
    Column(modifier.clip(RoundedCornerShape(16.dp)).background(colors.background.copy(alpha = 0.4f))
        .padding(horizontal = 12.dp, vertical = 10.dp), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(7.dp)) {
            SwetIcon(icon, colors.textSecondary, null, size = 16.dp)
            Text(count.toString(), color = colors.textPrimary, fontWeight = FontWeight.SemiBold, style = MaterialTheme.typography.titleSmall)
        }
        Text(label, color = colors.textSecondary, style = MaterialTheme.typography.labelSmall)
    }
}

@Composable
private fun PicsoReactionButton(emoji: String, selected: Boolean, enabled: Boolean, onClick: () -> Unit) {
    val colors = SwetgramThemeTokens.colors
    val reduced = reduceMotion()
    // Emoji are pictograms, not labels; keep them inside the touch target at large font scales.
    val emojiSize = (25f / LocalDensity.current.fontScale).sp
    val fill by animateColorAsState(if (selected) colors.accent else colors.background.copy(alpha = 0.5f),
        swetTween(Motion.fast), label = "picsoReactionFill")
    val scale = animateFloatAsState(if (selected && !reduced) 1.08f else 1f, swetTween(Motion.normal), label = "picsoReactionScale")
    val label = stringResource(if (selected) Res.string.picso_remove_reaction else Res.string.picso_add_reaction, emoji)
    Box(Modifier.size(48.dp).graphicsLayer { scaleX = scale.value; scaleY = scale.value; alpha = if (enabled) 1f else 0.5f }
        .clip(CircleShape).background(fill).border(1.dp, if (selected) colors.accent else colors.borderStrong, CircleShape)
        .swetPressable(onClick, enabled = enabled, role = Role.Button, feedback = SwetPressFeedback.Scale)
        .semantics { this.selected = selected; contentDescription = label },
        contentAlignment = Alignment.Center) {
        Text(emoji, fontSize = emojiSize, modifier = Modifier.clearAndSetSemantics { })
    }
}

@Composable
private fun PicsoDockAction(
    icon: ImageVector, label: String, enabled: Boolean, onClick: () -> Unit, modifier: Modifier,
    selected: Boolean = false, primary: Boolean = false,
) {
    val colors = SwetgramThemeTokens.colors
    val fill = if (primary) colors.accent else colors.background.copy(alpha = 0.55f)
    val foreground = if (primary) colors.onAccent else colors.textPrimary
    Row(modifier.heightIn(min = 48.dp).clip(RoundedCornerShape(18.dp)).background(fill)
        .border(1.dp, if (primary) colors.accent else colors.borderStrong, RoundedCornerShape(18.dp))
        .swetPressable(onClick, enabled = enabled, feedback = SwetPressFeedback.Scale)
        .semantics { this.selected = selected }
        .graphicsLayer { alpha = if (enabled) 1f else 0.5f }.padding(horizontal = 10.dp, vertical = 12.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp, Alignment.CenterHorizontally), verticalAlignment = Alignment.CenterVertically) {
        SwetIcon(icon, foreground, null, size = 20.dp)
        Text(label, color = foreground, style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.SemiBold,
            modifier = Modifier.weight(1f, fill = false))
    }
}
